import { createSelector } from "@reduxjs/toolkit"
import ChatHelpers from "../lib/chatHelpers"
import MessageHelpers from "../lib/messageHelpers"
import { Chat, SimpleChat } from "./chatsSlice"
import {
  ChatMessages,
  GenericMessage,
  Message,
  MessageRetractionsMap,
  StopUpdate,
} from "./messagesSlice"
import { RootState } from "./store"
import { getSlickshiftAIJid } from "../jidUtils"
import { isTranschatRoomJid } from "../lib/privateAgentUtils"
import { JID } from "@xmpp/jid"
import posthog from "posthog-js"
import {
  buildIsSlickAISilenced,
  SILENT_SLICK_AI_FLAG,
} from "../lib/slickAISilence"
import { selectAccountChats } from "./accountChatsSlice"

// An input selector and not an imperative read inside the result functions:
// PostHog resolves the flag after the first render, and a memo keyed only on
// chats/messages would keep serving counts computed while it was still off.
const selectSlickAISilenceFlag = () =>
  !!posthog.isFeatureEnabled(SILENT_SLICK_AI_FLAG)

/**
 * Turns reselect v5's argument cache off for the count selectors. That cache
 * returns the previous result for the same state object without ever calling
 * the input selectors — which would hide a flag flip, since nothing in the
 * store moves when PostHog resolves one. The result memo still holds.
 */
const readInputsEveryCall = <F>(fn: F) => fn

const isSlickAISilencedIn = (
  accountChats: ReturnType<typeof selectAccountChats>,
) => buildIsSlickAISilenced(selectSlickAISilenceFlag(), accountChats)

/**
 * The same predicate, built from the whole state — for thunks, which hold a
 * state and not a set of selector inputs. One decision function either way.
 */
export const selectIsSlickAISilenced = (state: RootState) =>
  isSlickAISilencedIn(selectAccountChats(state))

export const selectDriverParticipant = (chatJid: string | undefined) => {
  return (state: RootState) => {
    const chat = state.chats.find((c) => c.jid === chatJid)
    return chat ? chat.participants.find((p) => p.role === "driver") : undefined
  }
}

export type ChatWithMessages = ChatJid &
  Partial<Chat> & {
    messages: Message[]
  }

export type ChatSimplistic = ChatJid & {
  name: string
  active: boolean
  groups: string[]
}

export type ChatJid = {
  jid: string
}

export const selectChatMessagesWithAppliedRetractionsMemo = createSelector(
  [
    (state: RootState, chat: Chat) => state.messages[chat.jid].messages,
    (state: RootState, chat: Chat) =>
      state.messages[chat.jid].messageRetractions,
  ],
  (messages: Message[], messageRetractions: MessageRetractionsMap) => {
    return messages.map((message) => {
      let finalMessage = message
      const retraction = messageRetractions[message.id]
      if (retraction) {
        finalMessage = MessageHelpers.markRetracted(message)
      }

      return finalMessage
    })
  },
)

export const selectChatMessagesWithAppliedRetractions = (
  chatMessages: ChatMessages,
) => {
  return () => {
    return chatMessages.messages.map((message) => {
      let finalMessage = message
      const retraction = chatMessages.messageRetractions[message.id]
      if (retraction) {
        finalMessage = MessageHelpers.markRetracted(message)
      }

      return finalMessage
    })
  }
}

export const selectChats = (state: RootState) => {
  return state.chats
}

export const selectSlickshiftAIChat = (myJid: JID) =>
  createSelector(
    (state: RootState) => state.chats,
    (chats) => {
      const slickshiftAIJid = getSlickshiftAIJid(myJid)
      return chats.find((chat) => chat.jid === slickshiftAIJid)
    },
  )

export const selectMessages = (state: RootState) => {
  return state.messages
}

export const selectChatsSettings = (state: RootState) => {
  return state.chatsSettings
}

const chatSimplisticEquality = (
  prev: ChatSimplistic[],
  next: ChatSimplistic[],
): boolean => {
  if (prev.length !== next.length) return false

  for (let i = 0; i < prev.length; i++) {
    if (
      prev[i].jid !== next[i].jid ||
      prev[i].active !== next[i].active ||
      prev[i].name !== next[i].name
    )
      return false
  }

  return true
}

export const selectSimpleChats = createSelector(
  [selectChats],
  (chats: Chat[]) => {
    return chats.map((chat) => {
      return {
        name: chat.name,
        jid: chat.jid,
        active: chat.active,
        groups: chat.groups,
        draftMessage: chat.draftMessage,
      }
    }) as SimpleChat[]
  },
)

export const selectRoomChatCount = createSelector(
  (state: RootState) => state.chats,
  (chats) => {
    return chats.filter((chat) => chat.room).length
  },
)

export const selectActiveChatIndex = (state: RootState) => {
  if (state.chats.length === 0) return -1

  const index = state.chats.findIndex((chat) => chat.active)
  if (index === -1) return 0

  return index
}

export const selectMessageChat = (message: GenericMessage, userJid: string) =>
  createSelector(
    (state: RootState) => state.chats,
    (chats) => {
      return ChatHelpers.findMessageChat(message, chats, userJid)
    },
  )

export const selectActiveChat = createSelector(
  (state: RootState) => state.chats,
  (chats) => {
    const jid = chats
      .filter((chat) => chat.room)
      .find((chat) => chat.active)?.jid

    if (!jid) return chats[0]

    return chats.filter((chat) => chat.room).find((chat) => chat.jid === jid)!
  },
)

export const allUnreadCount = (myJid: string | undefined) => {
  return createSelector(
    [
      selectChats,
      selectMessages,
      selectChatsSettings,
      selectAccountChats,
      selectSlickAISilenceFlag,
    ],
    (chats, chatsMessages, chatsSettings, accountChats, flagEnabled) => {
      if (!myJid) return 0
      const isSilenced = buildIsSlickAISilenced(flagEnabled, accountChats)
      const unreadCounts = chats
        .filter((c) => c.room && !isTranschatRoomJid(c.jid))
        .map((chat) => {
          // Skip muted and archived chats by returning 0
          if (
            chatsSettings[chat.jid]?.muted ||
            chatsSettings[chat.jid]?.archived
          ) {
            return 0
          }

          return ChatHelpers.unreadMessagesCount(
            { ...chat, ...chatsMessages[chat.jid] },
            myJid,
            { silenceSlickAI: isSilenced(chat.jid) },
          )
        })

      return unreadCounts.reduce((acc, curr) => acc + curr, 0)
    },
    { argsMemoize: readInputsEveryCall },
  )
}

export const roomsUnreadCount = (myJid: string | undefined) => {
  return createSelector(
    [
      selectChats,
      selectMessages,
      selectChatsSettings,
      selectAccountChats,
      selectSlickAISilenceFlag,
    ],
    (chats, chatsMessages, chatsSettings, accountChats, flagEnabled) => {
      if (!myJid) return 0
      const isSilenced = buildIsSlickAISilenced(flagEnabled, accountChats)
      const unreadCounts = chats
        .filter((c) => c.room && !isTranschatRoomJid(c.jid))
        .map((chat) => {
          // Skip muted and archived chats by returning 0
          if (
            chatsSettings[chat.jid]?.muted ||
            chatsSettings[chat.jid]?.archived
          ) {
            return 0
          }

          return ChatHelpers.unreadMessagesCount(
            { ...chat, ...chatsMessages[chat.jid] },
            myJid,
            { silenceSlickAI: isSilenced(chat.jid) },
          )
        })

      return unreadCounts.reduce((acc, curr) => acc + (curr > 0 ? 1 : 0), 0)
    },
    { argsMemoize: readInputsEveryCall },
  )
}

export const chatLatestSentOrReceivedMessage = (
  chat: ChatMessages | undefined,
) => {
  return () => {
    if (!chat || chat.messages.length === 0) return undefined
    const candidates: Array<GenericMessage> = []

    candidates.push(chat.messages.slice(-1)[0])

    chat.messages.forEach((message) => {
      const marker = message.markers
        .filter((m) => !MessageHelpers.isSendingOrError(m))
        .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
        .slice(-1)[0]
      if (marker) {
        candidates.push(marker)
      }

      const reaction = message.reactions
        .filter((m) => !MessageHelpers.isSendingOrError(m))
        .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
        .slice(-1)[0]

      if (reaction) {
        candidates.push(reaction)
      }
    })

    const lastShare =
      chat?.messageIdsWithFileShared &&
      Object.values(chat.messageIdsWithFileShared)
        .map((share) => share)
        .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
        .slice(-1)[0]

    if (lastShare) {
      candidates.push(lastShare)
    }

    const lastDeliveryOrder =
      chat?.messageIdsWithDeliveryOrder &&
      Object.values(chat.messageIdsWithDeliveryOrder)
        .map((deliveryOrder) => deliveryOrder)
        .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
        .slice(-1)[0]

    if (lastDeliveryOrder) {
      candidates.push(lastDeliveryOrder)
    }

    const lastMessageRetraction =
      chat?.messageRetractions &&
      Object.values(chat.messageRetractions)
        .map((retraction) => retraction)
        .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
        .slice(-1)[0]

    if (lastMessageRetraction) {
      candidates.push(lastMessageRetraction)
    }

    return candidates.sort(
      (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp),
    )[0]
  }
}

export const latestSentOrReceivedMessageInIM = (state: RootState) => {
  const candidates: Array<GenericMessage> = []
  state.chats
    .filter((chat) => !chat.room)
    .forEach((chat) => {
      const latest = chatLatestSentOrReceivedMessage(state.messages[chat.jid])()

      if (latest) candidates.push(latest)
    })

  return candidates.sort(
    (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp),
  )[0]
}

export const hasMessageFileBeenShared = (
  chat: ChatMessages,
  messageId: string,
) => {
  if (!chat.messageIdsWithFileShared) return false

  return !!chat.messageIdsWithFileShared[messageId]
}

export const selectDocumentMetadata = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
) => {
  if (
    !chatMessages ||
    !messageId ||
    !chatMessages.messageIdsWithExtractedMetadata ||
    !chatMessages.messageIdsWithExtractedMetadata[messageId]
  )
    return undefined

  return chatMessages.messageIdsWithExtractedMetadata[messageId]
}

export const selectDocumentDriverContext = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
) => {
  if (
    !chatMessages ||
    !messageId ||
    !chatMessages.messageIdsWithDocumentDriverContext ||
    !chatMessages.messageIdsWithDocumentDriverContext[messageId]
  )
    return undefined

  return chatMessages.messageIdsWithDocumentDriverContext[messageId]
}

export const selectDeliveryOrder = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
) => {
  if (
    !chatMessages ||
    !messageId ||
    !chatMessages.messageIdsWithDeliveryOrder ||
    !chatMessages.messageIdsWithDeliveryOrder[messageId]
  )
    return undefined

  return chatMessages.messageIdsWithDeliveryOrder[messageId]
}

export const selectDocumentLinkByCmrMessageId = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
) => {
  if (
    !chatMessages ||
    !messageId ||
    !chatMessages.documentLinksByCmrMessageId ||
    !chatMessages.documentLinksByCmrMessageId[messageId]
  )
    return undefined

  return chatMessages.documentLinksByCmrMessageId[messageId]
}

export const selectDocumentLinkByDeliveryOrderMessageId = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
) => {
  if (
    !chatMessages ||
    !messageId ||
    !chatMessages.documentLinksByDeliveryOrderMessageId ||
    !chatMessages.documentLinksByDeliveryOrderMessageId[messageId]
  )
    return undefined

  return chatMessages.documentLinksByDeliveryOrderMessageId[messageId]
}

export const selectDocumentLinkByDeliveryOrderId = (
  chatMessages: ChatMessages | undefined,
  deliveryOrderId: string | undefined,
) =>
  deliveryOrderId
    ? chatMessages?.documentLinksByDeliveryOrderId?.[deliveryOrderId]
    : undefined

export const selectFastenOfType = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
  fastenType: string,
): any => {
  if (!messageId) return null
  if (!chatMessages) return null

  const fastens = chatMessages.fastens[messageId]
  if (!fastens) return null
  return fastens.find((f) => f.fastensWith === fastenType)
}

export const selectRouteUpdates = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
): null | StopUpdate[] => {
  const fasten = selectFastenOfType(chatMessages, messageId, "RouteUpdated")
  if (!fasten) return null

  return fasten.payload
}

export const selectHasRouteUnchanged = (
  chatMessages: ChatMessages | undefined,
  messageId: string | undefined,
): boolean => {
  const fasten = selectFastenOfType(chatMessages, messageId, "RouteUnchanged")

  return !!fasten
}

export type CardChat = {
  jid: string
  name: string
  groups: string[]
  draftMessage: string | undefined
  participants: Chat["participants"]
  active: boolean
}

export const selectChatJidByDriverJid = (driverJid: string) =>
  createSelector([selectChats], (chats) => {
    const chat = chats.find((chat) =>
      chat.participants.some(
        (participant) =>
          participant.role === "driver" && participant.jid === driverJid,
      ),
    )
    return chat?.jid
  })

export const selectChat =
  (jid: string) =>
  (state: RootState): Chat | undefined => {
    return state.chats.find((chat) => chat.jid === jid)
  }

export const selectChatCounts = (myJid: string) => {
  return createSelector(
    [selectChats, selectMessages, selectAccountChats, selectSlickAISilenceFlag],
    (chats, messages, accountChats, flagEnabled) => {
      const isSilenced = buildIsSlickAISilenced(flagEnabled, accountChats)
      const counter: Record<string, number> = {}
      return chats.reduce((acc, chat) => {
        acc[chat.jid] = ChatHelpers.unreadMessagesCount(
          { ...chat, ...messages[chat.jid] },
          myJid,
          { silenceSlickAI: isSilenced(chat.jid) },
        )
        return acc
      }, counter)
    },
    { argsMemoize: readInputsEveryCall },
  )
}

export const selectChatCountsForCurrentUser = createSelector(
  [selectChats, selectMessages, selectAccountChats, selectSlickAISilenceFlag],
  (chats, messages, accountChats, flagEnabled) => {
    const isSilenced = buildIsSlickAISilenced(flagEnabled, accountChats)
    const counter: Record<string, number> = {}
    return chats.reduce((acc, chat) => {
      acc[chat.jid] = ChatHelpers.unreadMessagesCount(
        { ...chat, ...messages[chat.jid] },
        window.myJID.toString(),
        { silenceSlickAI: isSilenced(chat.jid) },
      )
      return acc
    }, counter)
  },
  { argsMemoize: readInputsEveryCall },
)

export const selectConnectionStatus = createSelector(
  [(state: RootState) => state.chats],
  (chats) => {
    const result: Record<string, string | undefined> = {}
    return chats.reduce((acc, chat) => {
      acc[chat.jid] = chat.connectionStatus
      return acc
    }, result)
  },
)
